// Prompt 0455 — Tab Datos del AdminRolFuncionalPanel.
// Campos: nombre (req), descripcion, nivelMinimo, nivelMaximo, activo, codigo readonly.

import type React from 'react';
import { useState, useEffect } from 'react';
import {
  fetchPreviewCodigoRolFuncional,
  type RolFuncional,
  type NivelPuesto,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { ActiveSwitch } from '../../../../../../../shared/components/ActiveSwitch';
import { useRolFuncionalData } from '../hooks/useRolFuncionalData';

interface DatosTabProps {
  rol: RolFuncional | null;
  niveles: NivelPuesto[];
  isDirty: boolean;
  onMarkDirty: () => void;
  onMarkClean: () => void;
  onSaved: (createdId: string | null) => Promise<void> | void;
}

interface Form {
  nombre: string;
  descripcion: string;
  nivelMinimo: string;
  nivelMaximo: string;
  activo: boolean;
}

function buildForm(rol: RolFuncional | null): Form {
  return {
    nombre: rol?.nombre ?? '',
    descripcion: rol?.descripcion ?? '',
    nivelMinimo: rol?.nivelMinimo ?? '',
    nivelMaximo: rol?.nivelMaximo ?? '',
    activo: rol ? (rol.activo ?? true) : true,
  };
}

export function DatosTab({
  rol,
  niveles,
  isDirty,
  onMarkDirty,
  onMarkClean,
  onSaved,
}: DatosTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.rolFuncionalPanel ?? {}) as unknown as Record<string, string>;

  const [form, setForm] = useState<Form>(() => buildForm(rol));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewCodigo, setPreviewCodigo] = useState<string | null>(null);

  useEffect(() => {
    setForm(buildForm(rol));
  }, [rol]);

  // 0457 T-4 — Prellenar codigo RF-NNN para rol nuevo. Para rol existente mostramos (actualizado 0473/0476)
  // el codigo real (readonly siempre). Si falla el preview, mostramos '(pendiente)'
  // como fallback visual.
  useEffect(() => {
    if (rol) {
      setPreviewCodigo(null);
      return;
    }
    fetchPreviewCodigoRolFuncional()
      .then((p) => setPreviewCodigo(p.proximoCodigo))
      .catch(() => setPreviewCodigo('(pendiente)'));
  }, [rol]);

  const codigoMostrado = rol?.codigo ?? previewCodigo ?? '';

  const { guardar } = useRolFuncionalData();

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((prev) => {
      if (prev[key] === value) return prev;
      onMarkDirty();
      return { ...prev, [key]: value };
    });
    setError('');
  }

  async function handleSave() {
    if (!form.nombre.trim()) {
      setError((lbl.required as string | undefined) ?? 'Nombre obligatorio');
      return;
    }
    try {
      setSaving(true);
      const { createdId } = await guardar(rol?.id ?? null, {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || undefined,
        nivelMinimo: form.nivelMinimo || undefined,
        nivelMaximo: form.nivelMaximo || undefined,
        activo: form.activo,
      });
      onMarkClean();
      await onSaved(createdId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setForm(buildForm(rol));
    setError('');
    onMarkClean();
  }

  return (
    <div>
      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 6,
            fontSize: 12,
            color: '#DC2626',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16 }}>
          <div>
            <label style={lblStyle}>{lbl.codigo ?? 'Codigo'}</label>
            <input
              readOnly
              style={{
                ...inpStyle,
                fontFamily: "'JetBrains Mono', monospace",
                background: '#F8FAFC',
                color: '#5A6171',
              }}
              value={codigoMostrado}
              title={
                rol
                  ? (lbl.codigoAutogenerado ?? 'Codigo autogenerado')
                  : (lbl.codigoPreview ?? 'Preview · se asigna al guardar')
              }
            />
          </div>
          <div>
            <label style={lblStyle}>{lbl.nombre ?? 'Nombre'} *</label>
            <input
              style={inpStyle}
              value={form.nombre}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label style={lblStyle}>{lbl.descripcion ?? 'Descripcion'}</label>
          <textarea
            rows={3}
            style={{ ...inpStyle, resize: 'vertical' }}
            value={form.descripcion}
            onChange={(e) => set('descripcion', e.target.value)}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={lblStyle}>{lbl.nivelMinimo ?? 'Nivel minimo'}</label>
            <select
              style={selStyle}
              value={form.nivelMinimo}
              onChange={(e) => set('nivelMinimo', e.target.value)}
            >
              <option value="">—</option>
              {niveles.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.nombreCorto ?? n.nombre} ({n.codigo})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lblStyle}>{lbl.nivelMaximo ?? 'Nivel maximo'}</label>
            <select
              style={selStyle}
              value={form.nivelMaximo}
              onChange={(e) => set('nivelMaximo', e.target.value)}
            >
              <option value="">—</option>
              {niveles.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.nombreCorto ?? n.nombre} ({n.codigo})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label style={lblStyle}>{lbl.activo ?? 'Activo'}</label>
          <ActiveSwitch
            activo={form.activo}
            onChange={(v) => set('activo', v)}
            labelActive={lbl.activo ?? 'Activo'}
            labelInactive={lbl.inactivo ?? 'Inactivo'}
          />
        </div>
      </div>

      <div style={footerSticky}>
        <button
          onClick={handleCancel}
          disabled={!isDirty || saving}
          style={btnSecondary(!isDirty || saving)}
        >
          {panel.cancelar ?? 'Cancelar'}
        </button>
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          style={btnPrimary(!isDirty || saving)}
        >
          {saving ? (panel.saving ?? 'Guardando...') : (panel.guardar ?? 'Guardar')}
        </button>
      </div>
    </div>
  );
}

const lblStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const inpStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const selStyle: React.CSSProperties = {
  ...inpStyle,
  background: '#fff',
  appearance: 'auto',
};

const footerSticky: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  marginTop: 20,
  padding: '12px 0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  background: '#fff',
  borderTop: '1px solid #E5E7EB',
};

const btnSecondary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'transparent',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
  color: disabled ? '#9CA3AF' : '#374151',
  opacity: disabled ? 0.6 : 1,
});

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 24px',
  background: disabled ? '#94A3B8' : '#16A34A',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  fontFamily: 'inherit',
});
